import { Inject, Injectable } from '@angular/core';
import { IHttpBasicResponse } from '@boxx/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { PanelPageModel } from '../models/panel.model';
import {
  IPanelAnalyticsPayload,
  IPanelAnalyticsResponse,
  IPanelApiResponse,
  IPanelRepository,
  PANEL_REPOSITORY,
} from '../repositories/IPanel.repository';
import { IPanelService } from './IPanel.service';
import { ContactModel } from '@boxx/contacts-core';
import { EventModel } from '@boxx/events-core';
import { LeadModel, ReviewModel, ReviewsModel } from '@boxx/messages-core';
import { IEcwidDataStatistics } from '@boxx/gmb-core/lib/contracts/IEcwidData';

@Injectable({
  providedIn: 'root',
})
export class PanelService implements IPanelService {
  constructor(@Inject(PANEL_REPOSITORY) private repository: IPanelRepository) {}

  getPanelData(): Observable<PanelPageModel> {
    return this.repository
      .getPanelData()
      .pipe(
        map((response: IHttpBasicResponse<IPanelApiResponse>) =>
          PanelPageModel.fromApiResponse(response.data)
        )
      );
  }

  getPanelAnalytics(
    filter?: IPanelAnalyticsPayload
  ): Observable<any> {
    return this.repository
      .getPanelAnalytics(filter)
      .pipe(map((response) => response.data.attributes));
  }

  getPanelContacts(): Observable<any> {
    return this.repository.getPanelContacts().pipe(
      map((response: IHttpBasicResponse<any>) => {
        const contacts = response.data.map((c) =>
          ContactModel.fromDataResponse(c)
        );

        return contacts;
      })
    );
  }
  getPanelEvents(): Observable<any> {
    return this.repository.getPanelEvents().pipe(
      map((response: IHttpBasicResponse<any>) => {
        const events = response.data.map((ev) =>
          EventModel.fromDataResponse(ev)
        );

        return events;
      })
    );
  }

  getPanelLeads(): Observable<{
    messages: LeadModel[];
    totals: { total: number; seen: number; new: number };
  }> {
    return this.repository.getPanelLeads().pipe(
      map(
        (
          response: IHttpBasicResponse<{
            messages: any[];
            totals: { total: number; seen: number; new: number };
          }>
        ) => {
          const leads = response.data.messages.map((l) =>
            LeadModel.fromApiResponse(l)
          );

          return {
            messages: leads,
            totals: response.data.totals,
          };
        }
      )
    );
  }

  getPanelReviews(): Observable<ReviewsModel> {
    return this.repository.getPanelReviews().pipe(
      map((response) => {
        const payload = ReviewsModel.fromApiResponse(response.data);
        return payload;
      })
    );
  }

  getEcwidStadistics(): Observable<IEcwidDataStatistics> {
    return this.repository
      .getEcwidStadistics()
      .pipe(map((response) => response.data));
  }

  refreshEcwidStadistics(startDate: string, enDate: string): Observable<IEcwidDataStatistics> {
    return this.repository
      .refreshEcwidStadistics(startDate, enDate)
      .pipe(map((response) => (response.data) ? response.data : null));
  }

  activateNFC(payload: any): Observable<any> {
    return this.repository.activateNFC(payload).pipe(
      map((response) => response.data)
    );
  }

  getNFCDevices(): Observable<any> {
    return this.repository
      .getNFCDevices()
      .pipe(map((response) => response.data.attributes));
  }
}
